// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import EntityGuard from '../../../components/EntityGuard';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useSpearPartsApi } from '../../../store/api/hooks/spearPartsApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import Button from '../../../components/elements/button';
import { downloadExcel, downloadPDF } from '../../../_common/functions';
import toast from 'react-hot-toast';

function currencyCell(params: any) {
  const val = params.value;
  if (val == null || val === '' || val === '--') return <span style={{ color: '#9ca3af' }}>--</span>;
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function dateCell(params: any) {
  const row = params.data;
  if (!row) return '--';
  const d = row.modifiedDate || row.createdDate || row.addedDate;
  if (!d) return <span style={{ color: '#9ca3af' }}>--</span>;
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch {
    return d;
  }
}

function userCell(params: any) {
  const row = params.data;
  if (!row) return '--';
  const user = row.modifiedBy || row.addedBy || row.createdBy;
  if (!user) return <span style={{ color: '#9ca3af' }}>--</span>;
  return <span title={user}>{user}</span>;
}

const PartsVendorReport: React.FC = () => {
  const spearPartsApi = useSpearPartsApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExportingXls, setIsExportingXls] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const buildParams = (pageNum: number, filterParams: Record<string, any>) => {
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.vendor) params.VendorIDs = filterParams.vendor;
    if (filterParams.partCode) params.PartCode = filterParams.partCode;
    if (filterParams.partName) params.PartName = filterParams.partName;
    return params;
  };

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    spearPartsApi.getPartsVendorReport(
      buildParams(pageNum, filterParams),
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.totalRecord || d?.summary?.totalParts || 0);
        const vendorOpts = (d?.vendors || []).map((v: any) => ({ value: String(v.id), label: v.name }));
        const opts: any[] = [
          { key: 'partName', label: 'Part Name', type: 'text', options: [] },
          { key: 'partCode', label: 'Part Code', type: 'text', options: [] },
        ];
        if (vendorOpts.length) opts.push({ key: 'vendor', label: 'Vendor', options: vendorOpts });
        setFilterOptions(opts);
      },
      (error: any) => { console.error('Failed to fetch parts vendor report:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>) => {
    setFilters(filterMap);
    setCurrentPage(1);
    fetchData(1, filterMap);
  };

  const buildExportParams = () => {
    const params: any = {};
    if (filters.vendor) params.VendorIDs = filters.vendor;
    if (filters.partCode) params.PartCode = filters.partCode;
    if (filters.partName) params.PartName = filters.partName;
    return params;
  };

  const handleExportXls = () => {
    setIsExportingXls(true);
    spearPartsApi.exportPartsVendorReportToXLS(
      buildExportParams(),
      (resp: any) => { downloadExcel(resp, 'parts_vendor_report'); toast.success('Exported successfully!'); setIsExportingXls(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExportingXls(false); }
    );
  };

  const handleExportPdf = () => {
    setIsExportingPdf(true);
    spearPartsApi.exportPartsVendorReportToPDF(
      buildExportParams(),
      (resp: any) => { downloadPDF(resp, 'parts_vendor_report'); toast.success('Exported successfully!'); setIsExportingPdf(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExportingPdf(false); }
    );
  };

  const columnDefs = [
    { headerName: 'Part Name', field: 'partsName', headerClass: 'bg-[#f0f0f0]', pinned: 'left', minWidth: 180, flex: 2, cellRenderer: titleCell, headerTooltip: 'Part Name' },
    { headerName: 'Part Code', field: 'partsCode', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Part Code' },
    { headerName: 'Vendor Name', field: 'vendorName', headerClass: 'bg-[#f0f0f0]', minWidth: 160, flex: 2, cellRenderer: titleCell, headerTooltip: 'Vendor Name' },
    { headerName: 'Preferred', field: 'isPreferred', headerClass: 'bg-[#f0f0f0]', width: 100, cellRenderer: (p: any) => p.value ? <span style={{ color: '#2B5A2E', fontWeight: 600 }}>Yes</span> : <span style={{ color: '#9ca3af' }}>No</span>, headerTooltip: 'Preferred Vendor' },
    { headerName: 'Vendor Cost', field: 'costPerUnit', headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: currencyCell, headerTooltip: 'Cost Per Unit (this Vendor)' },
    { headerName: 'Avg Unit Cost', field: 'avgUnitCost', headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: currencyCell, headerTooltip: 'Average Cost Across All Vendors' },
    { headerName: 'Date', field: 'modifiedDate', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: dateCell, headerTooltip: 'Last Modified Date (or Created if not modified)' },
    { headerName: 'Modified / Added By', field: 'modifiedBy', headerClass: 'bg-[#f0f0f0]', minWidth: 150, flex: 1, cellRenderer: userCell, headerTooltip: 'Modified By or Added By' },
  ];

  return (
    <Layout>
      <EntityGuard entity="Parts & Preferred Vendor Report">
      <Header title="Parts &amp; Preferred Vendor Report" back="/reports">
        <Header.Right>
          <Button theme='secondary' onClick={handleExportPdf} disabled={isExportingPdf} style={{ marginRight: 8 }}>
            {isExportingPdf ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button theme='primary' onClick={handleExportXls} disabled={isExportingXls}>
            {isExportingXls ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} showDateRange={false} onApply={handleApplyFilter} />
       
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            onGridReady={(params) => params.api.sizeColumnsToFit()}
            onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>
      </EntityGuard>
    </Layout>
  );
};

export default PartsVendorReport;
